import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { withForm } from "../utils/wizardForm";
import { fmtDateUS } from "../utils/dates";

function CompanyDetailsContent({ companyId }) {
  const id = companyId;
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState(null);
  const [forms2290, setForms2290] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIds, setCheckingIds] = useState([]);
  const [acks, setAcks] = useState({}); // formId -> { loading, ok, error, open }

  // VIN, который нужно подсветить (если передан в query-параметре ?vin=...)
  const highlightVin = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get("vin") || "").trim().toUpperCase();
    return raw || null;
  }, [location.search]);

  // Свежие подачи сверху, старые ниже. Даты нет (черновик) — сортируем по id.
  const formsNewestFirst = useMemo(() => {
    const ts = (f) => {
      const d = f.submission_date ? new Date(f.submission_date).getTime() : NaN;
      return Number.isNaN(d) ? null : d;
    };
    return [...forms2290].sort((a, b) => {
      const ta = ts(a);
      const tb = ts(b);
      if (ta !== null && tb !== null && ta !== tb) return tb - ta;
      if (ta !== null && tb === null) return 1; // черновики без даты — наверх
      if (ta === null && tb !== null) return -1;
      return b.id - a.id;
    });
  }, [forms2290]);

  const getMonthFirstUsed = (form) => {
    const raw =
      form.month_first_used ??
      form.first_used_month ??
      form.first_used_date ??
      form.mfu ??
      null;

    if (!raw) return null;
    const d = new Date(raw);
    if (!isNaN(d)) {
      return d.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    return String(raw);
  };

  const paymentTypeLabel = (form) => {
    const code =
      form.irs_payment_option ??
      form.payment_type ??
      form.payment_option ??
      null;

    if (!code) return null;

    const map = {
      EFW: "Electronic Funds Withdrawal (EFW)",
      EFTPS: "EFTPS",
      CCDC: "Credit / Debit Card",
    };
    return map[code] ?? code;
  };

  const getSubmissionId = (irsObj) =>
    irsObj?.submission_id ?? irsObj?.submissionId ?? irsObj?.id;

  const fetchAckStub = async (formId, submissionId) => {
    const token = localStorage.getItem("token");
    setAcks((prev) => ({
      ...prev,
      [formId]: {
        ...(prev[formId] || {}),
        loading: true,
        ok: false,
        error: null,
      },
    }));
    try {
      const res = await fetch(`/api/irs/ack/${submissionId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`ACK request failed: ${res.status} ${text}`);
      }
      setAcks((prev) => ({
        ...prev,
        [formId]: { loading: false, ok: true, error: null, open: true },
      }));
    } catch (e) {
      setAcks((prev) => ({
        ...prev,
        [formId]: {
          loading: false,
          ok: false,
          error: e.message || "Unknown error",
          open: true,
        },
      }));
    }
  };

  const toggleAck = (formId) => {
    setAcks((prev) => ({
      ...prev,
      [formId]: { ...(prev[formId] || {}), open: !prev[formId]?.open },
    }));
  };

  // Fetch data on mount / when id changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [resCompany, resForms, resSubs] = await Promise.all([
          fetch(`/api/companies/${id}/`, { headers }),
          fetch(`/api/companies/${id}/form2290/`, { headers }),
          fetch(`/api/companies/${id}/submissions/`, { headers }),
        ]);

        if (!resCompany.ok) throw new Error("Failed to load company");
        if (!resForms.ok) throw new Error("Failed to load Form2290 history");
        if (!resSubs.ok) throw new Error("Failed to load IRS submissions");

        const [companyData, formsData, subsData] = await Promise.all([
          resCompany.json(),
          resForms.json(),
          resSubs.json(),
        ]);

        setCompany(companyData);
        setForms2290(formsData);
        setSubmissions(subsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleContinueForm2290 = async (formId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/form2290/${formId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error fetching form data");
      const data = await res.json();

      // step — номер следующей страницы мастера. Шаг 3 (способ оплаты выбран,
      // впереди сводка) раньше отсутствовал, и Continue на такой форме молча
      // не срабатывал.
      const stepUrlMap = {
        1: `/company/${id}/file-form/vehicles-tax`,
        2: `/company/${id}/file-form/irs-payment-options`,
        3: `/company/${id}/file-form/form-summary`,
        4: `/company/${id}/file-form/form-summary`,
      };
      const url = stepUrlMap[data.step] || stepUrlMap[1];
      navigate(withForm(url, formId));
    } catch (err) {
      console.error("Continue Form2290 error:", err);
    }
  };

  const handleResubmitForm2290 = async (formId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/form2290/${formId}/reopen/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to reopen form");
      }
      navigate(withForm(`/company/${id}/file-form/vehicles-tax`, formId));
    } catch (err) {
      console.error("Resubmit Form2290 error:", err);
      setError(err.message);
    }
  };

  const handleDeleteForm2290 = async (formId) => {
    const form = forms2290.find((f) => f.id === formId);
    if (!form || form.status !== "incomplete") {
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to delete this form? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/form2290/${formId}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ message: "Failed to delete form." }));
        throw new Error(errorData.message || `Error: ${res.status}`);
      }

      setForms2290((currentForms) =>
        currentForms.filter((form) => form.id !== formId),
      );
    } catch (err) {
      console.error("Delete Form2290 error:", err);
      setError(err.message);
    }
  };

  const handleCheckStatus = (formId) => {
    setCheckingIds((ids) => [...ids, formId]);
    setTimeout(() => {
      setCheckingIds((ids) => ids.filter((i) => i !== formId));
    }, 2000);
  };

  const handleDownload = async (submissionId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `/api/companies/${id}/submissions/${submissionId}/download/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Error downloading PDF: ${res.status}`);

      const cd = res.headers.get("Content-Disposition") || "";
      let filename = `${submissionId}.pdf`;
      const match = cd.match(/filename\*?=([^;]+)/i);
      if (match) {
        filename = match[1]
          .replace(/UTF-8''/, "")
          .trim()
          .replace(/(^"|"$)/g, "");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const handleDownloadFilled = async (formId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `/api/companies/${id}/form2290/${formId}/download-filled/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok)
        throw new Error(`Error downloading filled PDF: ${res.status}`);

      const cd = res.headers.get("Content-Disposition") || "";
      let filename = `Form2290_${formId}_filled.pdf`;
      const match = cd.match(/filename\*?=([^;]+)/i);
      if (match) {
        filename = match[1]
          .replace(/UTF-8''/, "")
          .trim()
          .replace(/(^"|"$)/g, "");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Download filled PDF error:", err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto bg-white shadow-lg overflow-hidden">
      {loading ? (
        <div className="p-6 text-center text-gray-600">Loading...</div>
      ) : error ? (
        <div className="p-6 text-center text-red-500">{error}</div>
      ) : (
        <div className="p-6 space-y-8">
          {/* Company Info */}
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
              <h1 className="text-2xl font-bold text-[#0F2C5E]">
                Company Details
              </h1>
              <button
                onClick={() => navigate(`/company/${id}/edit`)}
                className="mt-2 sm:mt-0 bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition text-sm"
              >
                Edit Business Info
              </button>
            </div>
            <div className="space-y-1 text-gray-800 text-sm">
              <p>
                <span className="font-semibold">Name:</span> {company.name}
              </p>
              <p>
                <span className="font-semibold">EIN:</span> {company.EIN}
              </p>
            </div>
          </div>

          {/* Form 2290 History */}
          <section className="border">
            <div className="bg-gray-100 px-4 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <h2 className="text-xl font-semibold text-[#0F2C5E]">
                Tax Submission History - Form 2290
              </h2>
              <button
                onClick={() => navigate(`/company/${id}/file-form`)}
                className="mt-2 sm:mt-0 bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition text-sm"
              >
                File Form 2290
              </button>
            </div>

            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="border px-3 py-2 text-left">#</th>
                    <th className="border px-3 py-2 text-left">Date</th>
                    <th className="border px-3 py-2 text-left">Ref No</th>
                    <th className="border px-3 py-2 text-left">
                      Tax Period
                    </th>
                    <th className="border px-3 py-2 text-left">
                      Month First Used
                    </th>
                    <th className="border px-3 py-2 text-left">
                      Payment Type
                    </th>
                    <th className="border px-3 py-2 text-left">Status</th>
                    <th className="border px-3 py-2 text-left">Vehicles</th>
                    <th className="border px-3 py-2 text-left">
                      Tax Amount
                    </th>
                    <th className="border px-3 py-2 text-center">
                      Submitted Return
                    </th>
                    <th className="border px-3 py-2 text-center">
                      Acknowledgments
                    </th>
                    <th className="border px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {forms2290.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center px-3 py-6">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    formsNewestFirst.map((form, idx) => {
                      const matchedSubs = submissions
                        .filter(
                          (s) =>
                            s.form2290 === form.id ||
                            s.form2290_id === form.id,
                        )
                        .slice()
                        .sort((a, b) => b.id - a.id);
                      const irs =
                        matchedSubs.find((s) => s.has_error === false) ||
                        matchedSubs[0];
                      const isChecking = checkingIds.includes(form.id);

                      const vehicleList =
                        form.vehicles_detail &&
                        Array.isArray(form.vehicles_detail)
                          ? form.vehicles_detail
                          : Array.isArray(form.vehicles)
                          ? form.vehicles
                          : [];

                      const hasHighlightedVin =
                        highlightVin &&
                        Array.isArray(vehicleList) &&
                        vehicleList.some((v) =>
                          (v.vin || "")
                            .toString()
                            .toUpperCase()
                            .includes(highlightVin),
                        );

                      return (
                        <tr
                          key={form.id}
                          className={`${
                            idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          } ${
                            hasHighlightedVin
                              ? "ring-2 ring-yellow-400 bg-yellow-50"
                              : ""
                          }`}
                        >
                          <td className="border px-3 py-2">{idx + 1}</td>
                          <td className="border px-3 py-2">
                            {fmtDateUS(form.submission_date, "-")}
                          </td>
                          <td className="border px-3 py-2">{form.id}</td>
                          <td className="border px-3 py-2">
                            {form.tax_period}
                          </td>
                          <td className="border px-3 py-2">
                            {getMonthFirstUsed(form) ?? "-"}
                          </td>
                          <td className="border px-3 py-2">
                            {paymentTypeLabel(form) ?? "-"}
                            {form.fee_card_last4 && (
                              <div className="text-xs text-gray-500 whitespace-nowrap">
                                Fee card &bull;&bull; {form.fee_card_last4}
                              </div>
                            )}
                          </td>
                          <td className="border px-3 py-2">
                            {form.status}
                          </td>
                          <td className="border px-3 py-2">
                            {form.vehicles.length}
                          </td>
                          <td className="border px-3 py-2">
                            {form.total_tax_due != null
                              ? `$${form.total_tax_due}`
                              : "-"}
                          </td>
                          <td className="border px-3 py-2 text-center">
                            {form.filled_pdf_base64 ? (
                              <button
                                onClick={() =>
                                  handleDownloadFilled(form.id)
                                }
                                className="bg-indigo-600 text-white px-2 py-1 hover:bg-indigo-700 transition text-xs"
                              >
                                Download
                              </button>
                            ) : (
                              <span className="text-gray-500">–</span>
                            )}
                          </td>
                          <td className="border px-3 py-2 text-center">
                            {form.status === "rejected" ? (
                              irs ? (
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/company/${id}/forms/${
                                        form.id
                                      }/ack/${getSubmissionId(irs)}`,
                                    )
                                  }
                                  className="bg-rose-600 text-white px-2 py-1 hover:bg-rose-700 transition text-xs"
                                >
                                  View ACK
                                </button>
                              ) : (
                                <span className="text-gray-500">–</span>
                              )
                            ) : (
                              <span className="text-gray-500">–</span>
                            )}
                          </td>
                          {/* --- Action column --- */}
                          <td className="border px-3 py-2 text-center">
                            {form.status === "rejected" && (
                              <button
                                onClick={() =>
                                  handleResubmitForm2290(form.id)
                                }
                                className="bg-rose-600 text-white px-2 py-1 hover:bg-rose-700 transition text-xs"
                              >
                                Resubmit
                              </button>
                            )}

                            {form.status === "incomplete" && (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() =>
                                    handleContinueForm2290(form.id)
                                  }
                                  className="bg-blue-600 text-white px-2 py-1 hover:bg-blue-700 transition text-xs"
                                >
                                  Continue
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteForm2290(form.id)
                                  }
                                  className="bg-red-600 text-white px-2 py-1 hover:bg-red-700 transition text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            )}

                            {form.status === "complete" &&
                              (irs && irs.has_error === false ? (
                                <button
                                  onClick={() => handleDownload(irs.id)}
                                  className="bg-green-600 text-white px-2 py-1 hover:bg-green-700 transition text-xs"
                                >
                                  Download
                                </button>
                              ) : (
                                <span className="text-gray-500 text-xs">
                                  No File
                                </span>
                              ))}

                            {form.status === "send" && (
                              <button
                                onClick={() => handleCheckStatus(form.id)}
                                disabled={isChecking}
                                className="flex items-center mx-auto bg-yellow-500 disabled:opacity-50 text-white px-2 py-1 hover:bg-yellow-600 transition text-xs"
                              >
                                {isChecking && (
                                  <svg
                                    className="w-4 h-4 mr-1 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4z"
                                    />
                                  </svg>
                                )}
                                Check Status
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyDetailsContent;
